import { useEffect, useState } from "react";
import axios from "axios";
import Navigation from "@/components/Navigation";
import { Activity } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LockedPanel } from "@/components/entitlements/LockedPanel";
import { SignalsOverview } from "@/components/signals/SignalsOverview";
import { MovementRow } from "@/components/signals/MovementRow";
import { PolicyShiftPanel } from "@/components/signals/PolicyShiftPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIGNAL_OPTIONS = [
  { value: "", label: "All Signals" },
  { value: "shift-up", label: "Surges" },
  { value: "shift-down", label: "Drops" },
  { value: "stalled", label: "Stalled" },
];

const CHAMBER_OPTIONS = [
  { value: "", label: "Both Chambers" },
  { value: "House", label: "House" },
  { value: "Senate", label: "Senate" },
];

const BILL_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "hr", label: "H.R." },
  { value: "s", label: "S." },
  { value: "hjres,sjres", label: "Joint Res" },
  { value: "hres,sres,hconres,sconres", label: "Other Res" },
];

const FilterGroup = ({ label, options, value, onChange, testPrefix }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-white/35 w-16 shrink-0">
      {label}
    </span>
    {options.map((o) => (
      <button
        key={o.value || "all"}
        type="button"
        onClick={() => onChange(o.value)}
        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
          value === o.value
            ? "border-orange-500/60 bg-orange-500/15 text-orange-200 font-semibold"
            : "border-white/15 text-white/55 hover:text-orange-200 hover:border-orange-500/40"
        }`}
        data-testid={`${testPrefix}-${(o.label || "all").replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/** Signals, long-form view of every probability shift / stalled signal.
 *  Same data as the Live Signals strip on the landing dashboard, but with
 *  more detail, a higher item cap, and signal/chamber/bill-type filters.
 */
export default function Signals() {
  const { featureMode } = useAuth();
  const [signals, setSignals] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signalType, setSignalType] = useState("");
  const [chamber, setChamber] = useState("");
  const [billType, setBillType] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = { limit: 40 };
    if (signalType) params.signal_type = signalType;
    if (chamber) params.chamber = chamber;
    if (billType) params.bill_type = billType;
    const { limit, ...filters } = params;
    Promise.all([
      axios.get(`${API}/dashboard/signals`, { params }).then((r) => r.data || []).catch(() => []),
      axios.get(`${API}/dashboard/signals/overview`, { params: filters }).then((r) => r.data).catch(() => null),
    ])
      .then(([list, ov]) => { setSignals(list); setOverview(ov); })
      .finally(() => setLoading(false));
  }, [signalType, chamber, billType]);

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }} data-testid="signals-page">
      <Navigation />

      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3 mb-1">
            <Activity className="w-10 h-10 text-orange-400" />
            <div>
              <div className="flex items-center gap-2">
                <span className="whip-pill whip-pill-orange">Signals</span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-white/40">Live · Forecast cache</span>
              </div>
              <h1 className="text-4xl font-black tracking-tight text-white mt-1" data-testid="signals-title">
                Where the floor is shifting
              </h1>
            </div>
          </div>
          <p className="text-sm text-white/55 max-w-2xl mt-2">
            Every bill whose WHIP pass probability has moved 5+ points since we first scored it, plus bills quietly
            stalling. Each one shows where it started, where it is now, and the specific events that moved it.
          </p>

          <div className="mt-5 space-y-2.5" data-testid="signals-filters">
            <FilterGroup
              label="Signal"
              options={SIGNAL_OPTIONS}
              value={signalType}
              onChange={setSignalType}
              testPrefix="signals-filter-signal"
            />
            <FilterGroup
              label="Chamber"
              options={CHAMBER_OPTIONS}
              value={chamber}
              onChange={setChamber}
              testPrefix="signals-filter-chamber"
            />
            <FilterGroup
              label="Type"
              options={BILL_TYPE_OPTIONS}
              value={billType}
              onChange={setBillType}
              testPrefix="signals-filter-type"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto mb-3"></div>
            <p className="text-white/55">Polling intelligence feeds…</p>
          </div>
        )}

        {!loading && signals.length === 0 && (
          <p className="text-center text-white/55 py-16" data-testid="signals-empty">
            No signals match these filters.
          </p>
        )}

        {!loading && signals.length > 0 && (
          <div className="space-y-6">
            <SignalsOverview overview={overview} />
            <div className="grid gap-6 lg:grid-cols-3">
              <section className="lg:col-span-2 space-y-3" data-testid="signals-list">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60">Movement board</h2>
                  <p className="text-[11px] font-mono text-white/35" data-testid="signals-result-count">
                    {signals.length}{overview?.total > signals.length ? ` OF ${overview.total}` : ""} SIGNAL{(overview?.total || signals.length) === 1 ? "" : "S"} · BIGGEST MOVE FIRST
                  </p>
                </div>
                {signals.map((s, i) => <MovementRow key={s.billId + i} s={s} index={i} />)}
              </section>
              <aside className="lg:col-span-1">
                <PolicyShiftPanel overview={overview} />
              </aside>
            </div>
            {featureMode("live_signals") === "preview" && (
              <LockedPanel
                feature="live_signals"
                tier="pro"
                title="Full Live Signals Feed"
                blurb="You're seeing the most recent signals. WHIP Pro unlocks the complete feed, every probability surge, drop, and stalled-bill alert across Congress."
                className="mt-8"
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
